import { DepressionContentPage } from "@/components/DepressionContentPage";

const InsuranceForTmsDepressionTreatment = () => (
  <DepressionContentPage
    seo={{
      title:
        "Does Insurance Cover TMS & Advanced Depression Treatment? | Neuro Wellness",
      description:
        "Insurance coverage for TMS, SAINT®, and SPRAVATO® depends on your plan, diagnosis, and medical necessity. Learn how verification and prior authorization work.",
      canonical: "/insurance-for-tms-depression-treatment",
      keywords:
        "insurance cover TMS, TMS insurance coverage, SAINT insurance, SPRAVATO insurance, prior authorization, Coral Springs",
    }}
    badge="Insurance Coverage"
    heroTitle="Does Insurance Cover TMS and Advanced Depression Treatment?"
    heroSubtitle="Insurance coverage for advanced depression treatment depends on several factors. Here's what influences whether your plan may cover treatment — and how our team can help you find out."
    sections={[
      {
        heading: "What Insurance Coverage Depends On",
        variant: "cards",
        cards: [
          {
            title: "The Treatment",
            body: "Different treatments have different coverage profiles. TMS, SAINT®, and SPRAVATO® may be handled differently by the same plan.",
          },
          {
            title: "Your Diagnosis",
            body: "Coverage typically requires a qualifying diagnosis, such as major depressive disorder.",
          },
          {
            title: "Previous Treatment History",
            body: "Many plans require documentation that prior treatments, such as antidepressants, were tried without adequate response.",
          },
          {
            title: "Medical Necessity",
            body: "Insurers require documentation that the treatment is medically necessary for your situation.",
          },
          {
            title: "Your Specific Plan",
            body: "Even within the same insurer, different plans have different benefits and requirements.",
          },
          {
            title: "Prior Authorization",
            body: "Many plans require prior authorization before treatment begins. We handle this on your behalf.",
          },
        ],
      },
      {
        heading: "How the Verification Process Works",
        variant: "steps",
        steps: [
          {
            title: "You provide your insurance information",
            body: "We collect your plan details through a short form or over the phone.",
          },
          {
            title: "We verify your benefits",
            body: "Our team contacts your insurer to determine what your plan covers and what requirements apply.",
          },
          {
            title: "We handle prior authorization",
            body: "If required, we submit the authorization request with supporting documentation.",
          },
          {
            title: "We explain your cost",
            body: "Before treatment begins, we tell you what your out-of-pocket cost is likely to be.",
          },
          {
            title: "We appeal if needed",
            body: "If a claim is denied, we file an appeal on your behalf with additional documentation.",
          },
        ],
      },
      {
        heading: "What You Can Prepare in Advance",
        variant: "text",
        body: (
          <p>
            To help the verification process go smoothly, it's useful to have a
            rough list of medications you've tried, approximate dates, and what
            happened. You don't need complete records for the first call — we
            can gather details as the process moves forward.
          </p>
        ),
      },
      {
        heading: "Carriers We Work With",
        variant: "text",
        body: (
          <p>
            We work with a range of insurance carriers. Coverage varies by
            individual plan, even within the same carrier. The most reliable way
            to know whether your specific plan covers treatment is to let our
            team verify your benefits directly.
          </p>
        ),
      },
    ]}
    faq={[
      {
        question: "Does insurance cover TMS?",
        answer:
          "Many plans cover TMS for treatment-resistant depression when medical necessity criteria are met. Coverage varies by plan. We verify your benefits before any financial commitment.",
      },
      {
        question: "Does insurance cover SAINT®?",
        answer:
          "Coverage for SAINT® varies by plan. Because it bills under newer codes, some plans are still developing their policies. We verify your benefits and handle prior authorization on your behalf.",
      },
      {
        question: "Does insurance cover SPRAVATO®?",
        answer:
          "Many commercial plans and Medicare cover SPRAVATO® for treatment-resistant depression with prior authorization. We verify your specific plan's coverage.",
      },
      {
        question: "What if my insurance denies coverage?",
        answer:
          "If a claim is denied, we file an appeal on your behalf with additional documentation. Many denials are due to incomplete information rather than genuine ineligibility.",
      },
    ]}
    relatedLinks={[
      {
        title: "Depression Treatment Cost",
        href: "/depression-treatment-cost",
      },
      { title: "How Insurance Works", href: "/how-insurance-works" },
      {
        title: "Depression Treatment Options",
        href: "/depression-treatment-options",
      },
      {
        title: "What Happens During a Consultation",
        href: "/depression-treatment-consultation",
      },
    ]}
    finalCtaHeading="Verify My Insurance Benefits"
    finalCtaText="Don't guess whether your plan covers treatment. Our team verifies your benefits and handles prior authorization so you know where you stand before you begin."
  />
);

export default InsuranceForTmsDepressionTreatment;
